import { useFlags } from "launchdarkly-react-client-sdk"
import React from "react"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout } from "#/features/global-ui/page-layout"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { Box, ForwardIcon, GetHelpIcon, Grid, Link, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { Link as RouterLink, Redirect } from "react-router-dom"

const PLACEHOLDER_TILE_COUNT = 4
const placeholderSubCategories: Array<{ title: string; link: string }> = [
  {
    title: "Sub Category 1",
    link: ""
  },
  {
    title: "Sub Category 2",
    link: ""
  },
  {
    title: "Sub Category 3",
    link: ""
  },
  {
    title: "Sub Category 4",
    link: ""
  }
]

export function Faqs(): JSX.Element {
  const { contentfulHelpCenter } = useFlags()

  if (!contentfulHelpCenter) {
    return <Redirect to="/" />
  }

  return <FaqsLandingPage />
}

function FaqsLandingPage(): JSX.Element {
  const intl = useIntl()
  return (
    <>
      <Title
        title={intl.formatMessage({
          defaultMessage: "FAQs",
          description:
            "Title for the Frequently Asked Questions page. Please do not translate the letters F-A-Q-S literally. Use the standard shorthand or acronym for 'Frequently Asked Questions' for the locale (e.g. the equivalent abbreviation that is used)."
        })}
      />
      <PageLayout
        header={
          <PageHeader
            // Confirm this is the correct glyph
            startGlyph={<GetHelpIcon sx={{ blockSize: "2.25rem", inlineSize: "2.25rem" }} />}
            pageTitle={
              <FormattedMessage
                defaultMessage="FAQs"
                description="Title for the Frequently Asked Questions page. Please do not translate the letters F-A-Q-S literally. Use the standard shorthand or acronym for 'Frequently Asked Questions' for the locale (e.g. the equivalent abbreviation that is used)."
              />
            }
          />
        }
      >
        <Grid container spacing={{ xs: 6, sm: 6, md: 7, lg: 7 }}>
          {/* This Array is just a placeholder for use during development and will be replaced with the actual categories */}
          {Array.from({ length: PLACEHOLDER_TILE_COUNT }).map((_, index) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={`Category ${index + 1}`}>
              <FaqsTile>
                {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx -- This is just a placeholder for use during development */}
                <FaqsCategoryLink title={`Category ${index + 1}`} />
                <FaqSubCategoriesList subCategories={placeholderSubCategories} />
              </FaqsTile>
            </Grid>
          ))}
        </Grid>
      </PageLayout>
    </>
  )
}

function FaqsCategoryLink({ title }: { title: string }): JSX.Element {
  return (
    <Box sx={{ marginBlockEnd: (theme) => theme.tokens.spacing.sm }}>
      <Link
        component={RouterLink}
        to={Paths.FAQS}
        variant="h4"
        endIcon={<ForwardIcon fontSize="medium" color="primary" sx={{ verticalAlign: "middle" }} />}
      >
        {title}
      </Link>
    </Box>
  )
}

function AdditionalTopicsLink({
  categoryLink,
  additionalTopicCount
}: {
  categoryLink: string
  additionalTopicCount: number
}): JSX.Element {
  return (
    <Link to={categoryLink} component={RouterLink} sx={{ textDecoration: "none" }}>
      <Typography variant="body2" color={(theme) => theme.palette.text.tertiary}>
        <FormattedMessage
          defaultMessage="+ {additionalTopicCount, plural, one {# more topic} other {# more topics}}"
          values={{ additionalTopicCount: additionalTopicCount }}
        />
      </Typography>
    </Link>
  )
}

function FaqSubCategoriesList({
  subCategories
}: {
  subCategories: Array<{ title: string; link: string }>
}): JSX.Element {
  const { showAdditionalTopics, additionalTopicCount, linksToShow } = {
    showAdditionalTopics: subCategories.length > 3,
    additionalTopicCount: subCategories.length - 3,
    linksToShow: subCategories.slice(0, 3)
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          marginBlockEnd: (theme) => theme.tokens.spacing.sm,
          gap: (theme) => theme.tokens.spacing.xs
        }}
      >
        {linksToShow.map((link) => (
          <Link key={link.title} component={RouterLink} to={link.link} variant="body2" margin={0} color="inherit">
            {link.title}
          </Link>
        ))}
      </Box>
      {showAdditionalTopics ? (
        <Box sx={{ marginInlineStart: (theme) => theme.tokens.spacing.sm }}>
          {/* Add the correct link to the additional topics */}
          <AdditionalTopicsLink categoryLink={""} additionalTopicCount={additionalTopicCount} />
        </Box>
      ) : null}
    </>
  )
}

function FaqsTile({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return <Tile sx={{ padding: theme.spacing(theme.tokens.spacing.xl), minBlockSize: "256px" }}>{children}</Tile>
}
